import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, OnInit, OnDestroy, Inject, NgZone, ViewChild, PLATFORM_ID } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { of, Subject, Subscription } from 'rxjs';
import { FORMAT_OPTIONS, ITRISearchFilterParam, SUBJECT_OPTIONS } from 'src/app/data-search/entity/itri-filter-search-param';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { HotKeywordsService } from '../../../../../share/service/hot-keywords.service';
import { GENERATOR_ENV_TOKEN, GeneratorEnv } from 'src/app/web-generator/generator-env-token';
import { AutoCompleteOptionsComponent } from 'src/app/share/component/auto-complete-options/auto-complete-options.component';
import { isPlatformBrowser } from '@angular/common';

/** 首頁上方的查詢視窗 */
@Component({
  selector: 'app-search-tool-modal',
  templateUrl: './search-tool-modal.component.html',
  styleUrls: [
    './index-search-04.component.scss',
    './search-tool-modal.component.scss'
  ]
})
export class SearchToolModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('skACO', {static: false}) skACO: AutoCompleteOptionsComponent;
  readonly FORMAT_OPTIONS = FORMAT_OPTIONS;
  readonly SUBJECT_OPTIONS = SUBJECT_OPTIONS;
  readonly ID = 'searchToolModal';
  /** 是否為瀏覽器端渲染 */
  isBrowser: boolean;
  /** 彈出視窗是否開啟中 */
  isOpened = false;
  /** 彈出視窗元素 */
  modalEl: HTMLElement;
  /** 分類 */
  searchSubject: string;
  /** 關鍵字 */
  searchKeyword: string;
  /** 關鍵字變化通知器 */
  protected searchKeyword$ = new Subject<string>();
  /** 關鍵字檢索選項 */
  keywordsOptions: string[] = [];
  /** 查詢資料表單即時篩選項目參數 */
  searchFilter: ITRISearchFilterParam = {};
  get hasAnySearchFilter() { return Object.keys(this.searchFilter).length; }
  /** 熱門關鍵字 */
  hotKeyWords: string[];
  /** 我查過的關鍵字(根據分頁紀錄，分頁結束就沒了) */
  myKeywordRecs: string[];
  private _autoColseSubc: Subscription;
  /** 關鍵字檢索訂閱 */
  private filterOptionSubc: Subscription;

  constructor(
    @Inject(GENERATOR_ENV_TOKEN) protected generatorEnv: GeneratorEnv,
    @Inject(PLATFORM_ID) protected platformId: object,
    protected router: Router,
    protected route: ActivatedRoute,
    protected hotKeywordsService: HotKeywordsService,
    protected el: ElementRef,
    protected http: HttpClient,
    protected zone: NgZone,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit() {
    this.hotKeywordsService.hotKeyWords$.subscribe(hotKeyWords => {
      this.hotKeyWords = hotKeyWords;
    });

    if (this.isBrowser) {
      this.hotKeywordsService.records$.subscribe(records => {
        this.myKeywordRecs = records;
      });

      this.filterOptionSubc = this.searchKeyword$.pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(value => {
          if (value) {
            value = value.trim();
            const body = new URLSearchParams();
            body.set('top', '10');
            if (value) { body.set('q', value); }
            return this.http.post<{ input: string; output: {[key: string]: number}[]; }>(`${this.generatorEnv.itriAutoCompleteApiHost}/v2/autocomplete`, body.toString(), {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
              }
            });
            // return of(( { input: value, output: [{'戰爭館': 12}, {"第二次世界大戰": 50}]} as { input: string; output: {[key: string]: number}[]; } ));
          } else {
            return of(( { input: value, output: []} as { input: string; output: {[key: string]: number}[]; } ));
          }
        }),
        map(v => {
          return v.output.map(v2 => Object.keys(v2)[0]);
        }),
      ).subscribe(options => {
        this.keywordsOptions = options;
      });
    }
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      this.modalEl = (this.el.nativeElement as HTMLElement).querySelector('.modal');

      $(this.modalEl).on('hidden.bs.modal', (event) => {
        this.onClosed(event);
      });

      $(this.modalEl).on('shown.bs.modal', (event) => {
        this.onShown(event);
      });

      this._autoColseSubc = this.router.events.subscribe((evt) => {
        if (!(evt instanceof NavigationEnd)) {
          return;
        }
        if (this.isOpened) {
          this.close();
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this._autoColseSubc) {
      this._autoColseSubc.unsubscribe();
      delete this._autoColseSubc;
    }
    if (this.filterOptionSubc) {
      this.filterOptionSubc.unsubscribe();
      delete this.filterOptionSubc;
    }
  }

  /** 開啟視窗 */
  open() {
    $(this.modalEl).modal('show');
  }

  /** 關閉視窗 */
  close() {
    $(this.modalEl).modal('hide');
  }

  /** 當視窗被打開 */
  onShown(event: any) {
    this.zone.run(() => {
      this.isOpened = true;
    });
  }

  /** 當視窗關閉 */
  onClosed(event: any) {
    this.zone.run(() => {
      this.isOpened = false;
      delete this.searchKeyword;
      delete this.searchSubject;
      this.searchFilter = {};
      this.searchKeyword$.next('');
      this.keywordsOptions = [];
    });
  }

  /** 當關鍵字詞改變 */
  onKeywordChange(value: string) {
    this.searchKeyword$.next(value);
  }

  /** 當關鍵字輸入框聚焦 */
  onFocusSearchKeywordInput() {
    this.skACO.isShowOptions = true;
  }

  /** 選取數位物件類型 */
  selectFormat(format: string) {
    if (this.searchFilter.format !== format) {
      this.searchFilter.format = format;
    }
  }

  /** 全選所有數位物件類型 */
  selectFormatAll() {
    if (this.searchFilter.format) {
      this.searchFilter.format = undefined;
    }
  }

  /** 點擊放大鏡按鈕做查詢 */
  onClickSearch(e?: UIEvent) {
    if (e) { e.preventDefault(); }
    const params: any = {};
    const query: any = {};
    if (this.searchKeyword) {
      params.keyword = this.searchKeyword;
    }
    if (this.searchSubject) {
      query.subject = this.searchSubject;
    }
    if (this.hasAnySearchFilter) {
      Object.assign(query, this.searchFilter);
    }
    if (Object.keys(query).length) {
      params.query = JSON.stringify(query);
    }

    this.router.navigate(['datasearch'], {
      queryParams: params,
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    });
  }
}
